import React, { useState, useEffect, useCallback, ChangeEvent, FormEvent } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { APIKey, APIKeyFormData, APIKeyCreateResponse, formatErrorMessage } from '../types';
import { useStrictModeGuard } from '../hooks/useStrictModeGuard';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faSave, faEdit, faTrash, faKey, faCopy, faEyeSlash, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import Pagination from './Pagination';

const APIKeys: React.FC = () => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { user } = useAuth();
  const { executeOnce } = useStrictModeGuard();
  const [apiKeys, setApiKeys] = useState<APIKey[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState<boolean>(false);
  const [editingKey, setEditingKey] = useState<APIKey | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage] = useState(10);
  const [formData, setFormData] = useState<APIKeyFormData>({
    name: '',
    expires_at: ''
  });
  const [newlyCreatedKey, setNewlyCreatedKey] = useState<APIKeyCreateResponse | null>(null);
  const [showKey, setShowKey] = useState<boolean>(false);
  const [copySuccess, setCopySuccess] = useState<boolean>(false);

  const fetchAPIKeys = useCallback(async () => {
    try {
      setLoading(true);
      const skip = (currentPage - 1) * itemsPerPage;
      const response = await axios.get('/api-keys', {
        params: { skip, limit: itemsPerPage }
      });
      const responseData = response.data;
      const keysData = responseData.items || responseData; // Handle both old and new format
      
      setApiKeys(keysData);
      
      // Update pagination info from response
      if (responseData.total_count !== undefined) {
        setTotalItems(responseData.total_count);
        setTotalPages(responseData.total_pages);
      }
    } catch (err) {
      setError(formatErrorMessage(err, 'Failed to fetch API keys'));
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage]);

  useEffect(() => {
    executeOnce(() => {
      fetchAPIKeys();
    });
  }, [fetchAPIKeys, executeOnce]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      if (editingKey) {
        await axios.put(`/api-keys/${editingKey.id}`, formData);
        await fetchAPIKeys();
        resetForm();
      } else {
        const response = await axios.post('/api-keys', formData);
        setNewlyCreatedKey(response.data);
        setShowKey(true);
        await fetchAPIKeys();
        // Don't call resetForm() here - let the user see the key first
        setFormData({ name: '', expires_at: '' });
        setShowForm(false);
        setEditingKey(null);
        setError(null);
      }
    } catch (err) {
      setError(formatErrorMessage(err, 'Failed to save API key'));
    }
  };

  const handleEdit = (key: APIKey) => {
    setEditingKey(key);
    setFormData({
      name: key.name,
      expires_at: key.expires_at || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (keyId: string) => {
    if (!window.confirm('Are you sure you want to delete this API key? This action cannot be undone.')) {
      return;
    }

    try {
      await axios.delete(`/api-keys/${keyId}`);
      await fetchAPIKeys();
    } catch (err) {
      setError(formatErrorMessage(err, 'Failed to delete API key'));
    }
  };

  const handleRegenerate = async (keyId: string) => {
    if (!window.confirm('Are you sure you want to regenerate this API key? The old key will no longer work.')) {
      return;
    }

    try {
      const response = await axios.post(`/api-keys/${keyId}/regenerate`);
      setNewlyCreatedKey(response.data);
      setShowKey(true);
      await fetchAPIKeys();
    } catch (err) {
      setError(formatErrorMessage(err, 'Failed to regenerate API key'));
    }
  };

  const copyToClipboard = async (text: string, isFullKey: boolean = false) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      setError('Failed to copy to clipboard');
    }
  };

  const resetForm = () => {
    setFormData({ name: '', expires_at: '' });
    setShowForm(false);
    setEditingKey(null);
    setError(null);
    setNewlyCreatedKey(null);
    setShowKey(false);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <div>Loading API keys...</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2 style={{ margin: 0, color: '#1f2937' }}>
          <FontAwesomeIcon icon={faKey} style={{ marginRight: '8px', color: '#3b82f6' }} />
          My API Keys
        </h2>
        <button
          onClick={() => setShowForm(true)}
          style={{
            padding: '10px 20px',
            backgroundColor: '#3b82f6',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '500',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <FontAwesomeIcon icon={faPlus} />
          Create API Key
        </button>
      </div>

      {error && (
        <div style={{
          padding: '12px',
          backgroundColor: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '8px',
          color: '#dc2626',
          marginBottom: '20px'
        }}>
          {error}
        </div>
      )}

      {copySuccess && (
        <div style={{
          padding: '12px',
          backgroundColor: '#f0fdf4',
          border: '1px solid #bbf7d0',
          borderRadius: '8px',
          color: '#166534',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <FontAwesomeIcon icon={faCopy} />
          Copied to clipboard!
        </div>
      )}

      {/* Newly Created Key Display */}
      {newlyCreatedKey && showKey && (
        <div style={{
          padding: '20px',
          backgroundColor: '#f0f9ff',
          border: '1px solid #0ea5e9',
          borderRadius: '12px',
          marginBottom: '20px'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h3 style={{ margin: 0, color: '#0c4a6e' }}>
              <FontAwesomeIcon icon={faKey} style={{ marginRight: '8px' }} />
              New API Key Created
            </h3>
            <button
              onClick={() => {
                setShowKey(false);
                setNewlyCreatedKey(null);
              }}
              style={{
                background: 'none',
                border: 'none',
                color: '#0c4a6e',
                cursor: 'pointer',
                fontSize: '16px'
              }}
              title="Close"
            >
              <FontAwesomeIcon icon={faEyeSlash} />
            </button>
          </div>
          <div style={{ marginBottom: '12px' }}>
            <strong>Name:</strong> {newlyCreatedKey.name}
          </div>
          <div style={{ marginBottom: '12px' }}>
            <strong>API Key:</strong>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginTop: '4px',
              padding: '8px',
              backgroundColor: '#ffffff',
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              fontFamily: 'monospace',
              fontSize: '12px',
              wordBreak: 'break-all'
            }}>
              <span style={{ flex: 1, userSelect: 'all' }}>{newlyCreatedKey.key}</span>
              <button
                onClick={() => copyToClipboard(newlyCreatedKey.key, true)}
                style={{
                  padding: '6px 12px',
                  backgroundColor: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: '500'
                }}
                title="Copy full API key"
              >
                <FontAwesomeIcon icon={faCopy} style={{ marginRight: '4px' }} />
                Copy Key
              </button>
            </div>
          </div>
          <div style={{
            padding: '8px 12px',
            backgroundColor: '#fef3c7',
            border: '1px solid #f59e0b',
            borderRadius: '6px',
            color: '#92400e',
            fontSize: '12px'
          }}>
            <FontAwesomeIcon icon={faExclamationTriangle} style={{ marginRight: '6px' }} />
            <strong>Important:</strong> This is the only time you'll see the full API key. Make sure to copy and store it securely.
          </div>
        </div>
      )}

      {/* API Key Form Modal */}
      {showForm && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={resetForm}
        >
          <div 
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              width: '90%',
              maxWidth: '500px',
              maxHeight: '90vh',
              overflow: 'auto'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              padding: '20px 24px',
              borderBottom: '1px solid #e5e7eb',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h3 style={{ margin: 0, color: '#1f2937' }}>
                {editingKey ? 'Edit API Key' : 'Create New API Key'}
              </h3>
            </div>

            <div style={{ padding: '0 24px 24px 24px' }}>
              <form onSubmit={handleSubmit}>
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                    Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                    placeholder="e.g., Production API Key, Development Key"
                  />
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>
                    Expiration Date (Optional)
                  </label>
                  <input
                    type="datetime-local"
                    name="expires_at"
                    value={formData.expires_at}
                    onChange={handleChange}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '14px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>

                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    onClick={resetForm}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#6b7280',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#3b82f6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <FontAwesomeIcon icon={editingKey ? faEdit : faSave} />
                    {editingKey ? 'Update' : 'Create'} API Key
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* API Keys List */}
      {apiKeys.length === 0 ? (
        <div style={{
          padding: '40px',
          textAlign: 'center',
          backgroundColor: '#f9fafb',
          borderRadius: '12px',
          border: '1px solid #e5e7eb'
        }}>
          <FontAwesomeIcon icon={faKey} style={{ fontSize: '48px', color: '#9ca3af', marginBottom: '16px' }} />
          <h3 style={{ margin: '0 0 8px 0', color: '#6b7280' }}>No API Keys</h3>
          <p style={{ margin: 0, color: '#9ca3af' }}>
            Create your first API key to start accessing your organization's resources programmatically. You can only see and manage API keys that you create.
          </p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '16px' }}>
          {apiKeys.map((key) => (
            <div key={key.id} style={{
              padding: '20px',
              backgroundColor: 'white',
              borderRadius: '12px',
              border: '1px solid #e5e7eb',
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                <div>
                  <h3 style={{ margin: '0 0 4px 0', color: '#1f2937', fontSize: '16px' }}>
                    {key.name}
                  </h3>
                  <div style={{ fontSize: '12px', color: '#6b7280', fontFamily: 'monospace' }}>
                    {key.key_prefix}...
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={() => handleEdit(key)}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: '#3b82f6',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '12px',
                      cursor: 'pointer'
                    }}
                  >
                    <FontAwesomeIcon icon={faEdit} />
                  </button>
                  <button
                    onClick={() => handleRegenerate(key.id)}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: '#f59e0b',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '12px',
                      cursor: 'pointer'
                    }}
                  >
                    Regenerate
                  </button>
                  <button
                    onClick={() => handleDelete(key.id)}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: '#ef4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      fontSize: '12px',
                      cursor: 'pointer'
                    }}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                </div>
              </div>
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', fontSize: '12px', color: '#6b7280' }}>
                <div>
                  <strong>Status:</strong> 
                  <span style={{ 
                    marginLeft: '4px',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    backgroundColor: key.is_active ? '#dcfce7' : '#fef2f2',
                    color: key.is_active ? '#166534' : '#dc2626',
                    fontSize: '11px'
                  }}>
                    {key.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div>
                  <strong>Created:</strong> {formatDate(key.created_at)}
                </div>
                {key.last_used_at && (
                  <div>
                    <strong>Last Used:</strong> {formatDate(key.last_used_at)}
                  </div>
                )}
                {key.expires_at && (
                  <div>
                    <strong>Expires:</strong> {formatDate(key.expires_at)}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {apiKeys.length > 0 && (
        <div className="card">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
            showInfo={true}
          />
        </div>
      )}
    </div>
  );
};

export default APIKeys;
